import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { CheckCircle2, MapPin, Search } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '../../components/layout/PageHeader';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Spinner } from '../../components/ui/Spinner';
import { listGbpLocations, selectGbpLocation, type GbpLocationOption } from './api';

// Community Inbox Phase 0b — the Google Business connect callback + location
// picker. Zernio redirects the AM's browser here after the Google consent
// (redirect_url carried our accountId; Zernio appends the pendingDataToken).
// The signed-in SPA lists the consenting user's GBP locations (server-side
// search — dealer groups can be large) and the AM picks THE location for this
// kyck account. The token stays in this page's memory and rides POST bodies.

export function GbpCallbackPage() {
  const [params] = useSearchParams();
  const accountId = params.get('accountId') ?? '';
  // Zernio's headless redirect token — accept the documented name plus the
  // legacy/temp variants (same tolerance as the Meta callback).
  const pendingDataToken =
    params.get('pendingDataToken') ?? params.get('pending_data_token') ?? undefined;
  const tempToken = params.get('tempToken') ?? params.get('temp_token') ?? undefined;

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selected, setSelected] = useState<GbpLocationOption | null>(null);
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(handle);
  }, [search]);

  const tokenPresent = Boolean(pendingDataToken || tempToken);
  const locations = useQuery({
    queryKey: ['gbp-locations', accountId, debouncedSearch],
    queryFn: () =>
      listGbpLocations({
        accountId,
        pendingDataToken,
        tempToken,
        search: debouncedSearch || undefined,
      }),
    enabled: Boolean(accountId) && tokenPresent && !connected,
  });

  const select = useMutation({
    mutationFn: (location: GbpLocationOption) =>
      selectGbpLocation({
        accountId,
        // select requires the pendingDataToken shape; the temp variant only
        // works for listing. If we only got a tempToken, surface the error.
        pendingDataToken: pendingDataToken ?? '',
        locationId: location.id,
        googleAccountId: location.googleAccountId ?? undefined,
      }),
    onSuccess: () => {
      setConnected(true);
      toast.success('Google Business Profile connected.');
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const missing = useMemo(() => {
    if (!accountId) return 'Missing the account reference.';
    if (!tokenPresent) return 'Missing the Google consent token.';
    return null;
  }, [accountId, tokenPresent]);

  return (
    <div className="mx-auto max-w-2xl">
      <PageHeader
        title="Connect Google Business"
        description="Pick the Google Business Profile location for this account."
        backHref={accountId ? `/accounts/${accountId}` : '/accounts'}
      />

      {missing ? (
        <p className="text-sm text-slate-600">
          {missing} Restart the connect flow from the account's API Connections section.
        </p>
      ) : connected ? (
        <div className="flex items-start gap-3 rounded-md border border-emerald-200 bg-emerald-50 p-4">
          <CheckCircle2 className="mt-0.5 h-5 w-5 text-emerald-600" />
          <div className="text-sm text-emerald-900">
            <p className="font-semibold">Google Business Profile connected</p>
            <p className="mt-0.5">
              {selected?.name} is now linked to this account's profile. Reviews arrive in the
              Community inbox in Phase 2.
            </p>
            <Button asChild size="sm" className="mt-3">
              <Link to={`/accounts/${accountId}`}>Back to the account</Link>
            </Button>
          </div>
        </div>
      ) : (
        <>
          <div className="relative mb-3">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search locations…"
              className="pl-8"
            />
          </div>

          {locations.isLoading ? (
            <div className="flex items-center gap-2 py-6 text-sm text-slate-500">
              <Spinner className="h-4 w-4" /> Loading Google Business locations…
            </div>
          ) : locations.isError ? (
            <p className="py-4 text-sm text-rose-600">
              {(locations.error as Error).message ||
                'Could not load locations — the consent may have expired. Restart the connect flow.'}
            </p>
          ) : (
            <div className="overflow-hidden rounded-lg border border-slate-200">
              {(locations.data?.locations ?? []).map((location) => (
                <button
                  key={location.id}
                  type="button"
                  onClick={() => setSelected(location)}
                  className={
                    selected?.id === location.id
                      ? 'block w-full border-b border-slate-100 bg-kyck-50 px-4 py-3 text-left'
                      : 'block w-full border-b border-slate-100 px-4 py-3 text-left hover:bg-slate-50'
                  }
                >
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 shrink-0 text-slate-400" />
                    <span className="min-w-0 flex-1 truncate text-sm font-medium text-slate-900">
                      {location.name}
                    </span>
                    {location.storeCode && (
                      <span className="text-xs text-slate-400">#{location.storeCode}</span>
                    )}
                  </div>
                  {(location.address || location.category) && (
                    <p className="ml-6 mt-0.5 truncate text-xs text-slate-500">
                      {[location.address, location.category].filter(Boolean).join(' · ')}
                    </p>
                  )}
                </button>
              ))}
              {(locations.data?.locations ?? []).length === 0 && (
                <p className="px-4 py-6 text-center text-sm text-slate-500">
                  No locations found{debouncedSearch ? ` for "${debouncedSearch}"` : ''}.
                </p>
              )}
              {locations.data?.hasMore && (
                <p className="border-t border-slate-100 bg-slate-50 px-4 py-2 text-xs text-slate-500">
                  More locations exist — narrow the search to find yours.
                </p>
              )}
            </div>
          )}

          <div className="mt-4 flex justify-end">
            <Button
              onClick={() => selected && select.mutate(selected)}
              disabled={!selected || select.isPending || !pendingDataToken}
              title={
                !pendingDataToken
                  ? 'The consent token type returned does not support selection — restart the flow.'
                  : undefined
              }
            >
              {select.isPending ? <Spinner className="h-4 w-4" /> : null}
              Connect {selected ? `"${selected.name}"` : 'selected location'}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
